/**
 * Custom tiptap-markdown serialize/parse rules for mention nodes.
 *
 * Serializes mention nodes as markdown links using absolute URLs:
 *   userMention  → [Label](https://origin/base/#/partner/:partnerId/user/:id)
 *   refMention   → [Label](https://origin/base/#/partner/:partnerId/ticket/:id)
 *                  (…/report/:id, …/vehicle/:id, …/thg/:id for the other ref types)
 *
 * This allows pasted browser URLs to be recognised as mentions and provides
 * native fallback rendering in any standard markdown viewer.
 *
 * On parse, markdown-it turns these into <a href="...">Label</a>.
 * The updateDOM hook converts matching <a> tags into <span data-type="..."> elements
 * so that TipTap's parseDOM picks them up as mention nodes.
 *
 * Supported URL shapes on parse:
 *   - Path-based:  …#/partner(s)/:partnerId/:refType/:refId
 *   - Query-based: …#/partner(s)/:partnerId/:refType?refTypeId=:refId
 *   - Legacy:      refType:refId  (backward compatibility)
 */

import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerModule } from "@/store/modules/partner";
import { MentionType } from "./mention-type.enum";

/** refType prefixes that are recognised as mention links */
const MENTION_PREFIXES = [
  BackendResourceEnum.USER,
  BackendResourceEnum.TICKET,
  BackendResourceEnum.REPORT,
  BackendResourceEnum.VEHICLE,
  BackendResourceEnum.THG
] as const;

type MentionPrefix = (typeof MENTION_PREFIXES)[number];

const PREFIXES_RE = MENTION_PREFIXES.join("|");

/**
 * Matches path-based hash-route URLs:
 *   …#/partner/:partnerId/:refType/:refId
 *   …#/partners/:partnerId/:refType/:refId
 *
 * Excludes ?, #, and whitespace from the captured refId so that
 * trailing query strings (e.g. ?tab=details) are not included.
 */
const URL_MENTION_PATH_RE = new RegExp(`#/partners?/[^/]+/(${PREFIXES_RE})/([^/?#\\s)]+)`);

/**
 * Matches query-based hash-route URLs:
 *   …#/partner/:partnerId/:refType?refTypeId=:refId
 *   …#/partners/:partnerId/:refType?(.*)
 */
const URL_MENTION_QUERY_RE = new RegExp(`#/partners?/[^/]+/(${PREFIXES_RE})\\?(.+)$`);

/**
 * Legacy regex that matches href values like "user:507f1f77bcf86cd799439011"
 */
const LEGACY_MENTION_HREF_RE = new RegExp(`^(${PREFIXES_RE}):(.+)$`);

/* ---------- helpers -------------------------------------------- */

function parseMentionHref(href: string): { prefix: MentionPrefix; id: string } | null {
  // Try URL path format: #/partner(s)/:partnerId/:refType/:refId
  const pathMatch = URL_MENTION_PATH_RE.exec(href);
  if (pathMatch) return { prefix: pathMatch[1] as MentionPrefix, id: pathMatch[2] };

  // Try URL query format: #/partner(s)/:partnerId/:refType?refTypeId=:refId
  const queryMatch = URL_MENTION_QUERY_RE.exec(href);
  if (queryMatch) {
    const refType = queryMatch[1] as MentionPrefix;
    const params = new URLSearchParams(queryMatch[2]);
    const id = params.get(`${refType}Id`);
    if (id) return { prefix: refType, id };
  }

  // Fall back to legacy refType:refId format
  const legacyMatch = LEGACY_MENTION_HREF_RE.exec(href);
  if (legacyMatch) return { prefix: legacyMatch[1] as MentionPrefix, id: legacyMatch[2] };

  return null;
}

function dataTypeForPrefix(prefix: MentionPrefix): string {
  return prefix === BackendResourceEnum.USER ? MentionType.USER : MentionType.REF;
}

/**
 * Build an absolute mention URL for the current environment.
 * Example: https://test.portal.schadensmeldung.digital/#/partner/607c.../ticket/640c...
 *
 * Falls back to legacy refType:refId when no partnerId is available.
 */
function buildMentionUrl(refType: string, refId: string): string {
  const partnerId = PartnerModule.partner?._id || PartnerModule.partner?.id || "";
  if (!partnerId) return `${refType}:${refId}`;

  const base = window.location.origin + window.location.pathname;

  return `${base}#/partner/${partnerId}/${refType}/${refId}`;
}

/* ---------- serialize ------------------------------------------ */

/**
 * Create a tiptap-markdown serialize spec for a mention node.
 * Usage: add to `addStorage()` on the TipTap extension.
 */
export function createMentionSerializeSpec(_triggerChar: string, getRefType?: (node: any) => string) {
  return {
    serialize(state: any, node: any) {
      const label: string = node.attrs.label ?? node.attrs.id;
      const refType: string = getRefType ? getRefType(node) : BackendResourceEnum.USER;
      const id: string = node.attrs.id;
      // Write as absolute URL so the link works natively in any markdown renderer
      // and matches what users copy from the browser address bar.
      state.write(`[${label}](${buildMentionUrl(refType, id)})`);
    }
  };
}

/* ---------- parse (updateDOM hook) ----------------------------- */

/**
 * tiptap-markdown parse spec shared by both mention extensions.
 * Converts <a href="…/partner/:id/ticket/:id">Label</a> into <span data-type="refMention" ...>.
 *
 * Should be added to ONE extension only (or guarded to run once) since it
 * operates on all matching <a> elements in the DOM.
 */
export function createMentionParseSpec() {
  return {
    updateDOM(element: HTMLElement) {
      const anchors = element.querySelectorAll("a");
      anchors.forEach((a) => {
        const href = a.getAttribute("href") ?? "";
        const parsed = parseMentionHref(href);
        if (!parsed) return;

        const span = element.ownerDocument.createElement("span");
        span.setAttribute("data-type", dataTypeForPrefix(parsed.prefix));
        span.setAttribute("data-id", parsed.id);

        const label = a.textContent ?? "";
        span.setAttribute("data-label", label);

        if (parsed.prefix !== BackendResourceEnum.USER) {
          span.setAttribute("data-ref-type", parsed.prefix);
        }

        span.textContent = label;
        a.replaceWith(span);
      });
    }
  };
}
